/** @file Sets the draw-order key at a frame: a full slot permutation, or null for the setup order. */
import { z } from 'zod';
import { isSlotPermutation } from '@pose2d/format/doc/isSlotPermutation';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { animationDraft } from './animationDraft';
import { insertByFrame } from './insertByFrame';
import { sameJson } from './sameJson';

/** animation.setDrawOrderKey: slot names or ids are stored as ids; one key per frame; the same order is a no-op. */
export const command = defineCommand({
  name: 'animation.setDrawOrderKey',
  description: 'Set the draw order at `frame`: `order` lists every slot (names or ids) exactly once, in the same sense as the setup drawOrder; null returns to the setup order.',
  input: z.object({ animation: z.string().min(1), frame: prim.frame, order: z.array(z.string().min(1)).nullable() }).strict(),
  apply(draft, input, ctx) {
    const { anim } = animationDraft(draft, input.animation, ctx);
    const order = input.order === null ? null : input.order.map((ref) => ctx.resolve('slot', ref));
    if (order !== null && !isSlotPermutation(draft, order)) {
      throwError('VALIDATION', undefined, { path: 'order', hint: `list each of the ${Object.keys(draft.slots).length} slots exactly once` });
    }
    const same = anim.drawOrder.find((k) => k.f === input.frame);
    if (same && sameJson(same.order, order)) return;
    if (same) same.order = order;
    else {
      insertByFrame(anim.drawOrder, { f: input.frame, order }, (next) => {
        anim.drawOrder = next;
      });
    }
    draft.meta.updatedAt = ctx.now;
  },
});
